import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CheckCircle2, Circle, X } from 'lucide-react';

import { useT } from '../i18n';
import { getApiClient } from '../api/factory';
import { getOnboardingChecklist, type OnboardingChecklist } from '../api/onboarding';

// ANX-067: dismiss персистится per-device (как notify-prefs ANX-053) — это
// напоминание об активации, не tenant-настройка. Скрытие у одного оператора
// не должно прятать чеклист у коллег.
const DISMISS_KEY = 'anexica.onboarding.dismissed.v1';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    // приватный режим — переживём повторный показ
  }
}

interface Step {
  done: boolean;
  label: string;
  hint: string;
  /** Маршрут-ссылка для невыполненного шага (если есть, куда вести). */
  to?: string;
}

function buildSteps(c: OnboardingChecklist, t: ReturnType<typeof useT>): Step[] {
  return [
    {
      done: c.widgetInstalled,
      label: c.widgetInstalled
        ? c.widgetDomain
          ? t('activation.step.widget.active', { domain: c.widgetDomain })
          : t('activation.step.widget.activeNoDomain')
        : t('activation.step.widget'),
      hint: t('activation.step.widget.hint'),
      to: '/sites',
    },
    {
      done: c.teamInvited,
      label: t('activation.step.team'),
      hint: t('activation.step.team.hint'),
      to: '/settings',
    },
    {
      done: c.officeHoursSet,
      label: t('activation.step.officeHours'),
      hint: t('activation.step.officeHours.hint'),
      to: '/sites',
    },
    {
      done: c.themeCustomized,
      label: t('activation.step.theme'),
      hint: t('activation.step.theme.hint'),
      to: '/sites',
    },
    {
      done: c.firstConversation,
      label: t('activation.step.conversation'),
      hint: t('activation.step.conversation.hint'),
    },
  ];
}

/**
 * ActivationChecklist — онбординг-чеклист активации tenant'а (ANX-067).
 * Показывается на пустом инбоксе, пока не выполнены все шаги или оператор
 * не скрыл его. Шаги вычисляются на backend из существующих сущностей
 * (виджет-heartbeat / операторы / часы / тема / диалоги).
 */
export function ActivationChecklist() {
  const t = useT();
  const [dismissed, setDismissed] = useState(isDismissed);

  const { data, isLoading } = useQuery({
    queryKey: ['onboarding'] as const,
    queryFn: () => getOnboardingChecklist(getApiClient()),
    // Чеклист дешёвый и быстро устаревает (виджет «загорается» сам) —
    // подтягиваем свежие данные при возврате на вкладку.
    staleTime: 30_000,
  });

  // Пока грузится, скрыт оператором, или всё готово — ничего не рисуем.
  if (isLoading || !data || dismissed || data.complete) return null;

  const steps = buildSteps(data, t);
  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);

  const dismiss = (): void => {
    markDismissed();
    setDismissed(true);
  };

  return (
    <div className="mx-auto w-full max-w-lg rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{t('activation.title')}</h2>
          <p className="mt-0.5 text-sm text-slate-500">{t('activation.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('activation.dismiss')}
          className="shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      <div className="mt-3">
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className="h-full rounded-full bg-accent-500 transition-all" style={{ width: `${pct}%` }} />
        </div>
        <p className="mt-1.5 text-xs font-medium text-slate-500">
          {t('activation.progress', { done: doneCount, total: steps.length })}
        </p>
      </div>

      <ul className="mt-4 flex flex-col gap-1">
        {steps.map((step) => (
          <li key={step.label}>
            <StepRow step={step} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepRow({ step }: { step: Step }) {
  const icon = step.done ? (
    <CheckCircle2 size={18} className="shrink-0 text-emerald-500" aria-hidden="true" />
  ) : (
    <Circle size={18} className="shrink-0 text-slate-300" aria-hidden="true" />
  );

  const body = (
    <div className="flex items-start gap-2.5">
      {icon}
      <div className="min-w-0">
        <p
          className={
            step.done ? 'text-sm text-slate-400 line-through' : 'text-sm font-medium text-slate-800'
          }
        >
          {step.label}
        </p>
        {!step.done && <p className="text-xs text-slate-500">{step.hint}</p>}
      </div>
    </div>
  );

  // Невыполненный шаг со ссылкой → кликабельная строка, ведущая в настройки.
  if (!step.done && step.to) {
    return (
      <Link to={step.to} className="block rounded-md px-2 py-1.5 hover:bg-slate-50">
        {body}
      </Link>
    );
  }
  return <div className="px-2 py-1.5">{body}</div>;
}
